import { OpenImgContextProvider } from 'openimg/react'
import { useEffect } from 'react'
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
	type LinkDescriptor,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from '#app/+types/root.ts'
import appleTouchIconAssetUrl from '#app/assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from '#app/assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { GoogleAnalytics } from '#app/components/google-analytics.tsx'
import { ProgressBar } from '#app/components/progress-bar.tsx'
import { useToast } from '#app/components/toaster.tsx'
import { Toaster } from '#app/components/ui/sonner.tsx'
import { useOptionalTheme } from '#app/routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from '#app/styles/tailwind.css?url'
import { getUserId } from '#app/utils/auth.ts'
import { ClientHintCheck, getHints } from '#app/utils/client-hints.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { pipeHeaders } from '#app/utils/headers.server.ts'
import { honeypot } from '#app/utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from '#app/utils/misc.tsx'
import { useNonce } from '#app/utils/nonce-provider.ts'
import { type Theme, getTheme } from '#app/utils/theme.server.ts'
import { makeTimings, time } from '#app/utils/timing.server.ts'
import { getToast } from '#app/utils/toast.server.ts'
import { getEnv } from '#server/env.server.ts'

export const links: Route.LinksFunction = () => {
	return [
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} satisfies LinkDescriptor,
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{
			title: data
				? 'Lydra AI - Pharmacovigilance Platform'
				: 'Error | Lydra AI',
		},
		{
			name: 'description',
			content: `AI-powered pharmacovigilance platform for managing adverse event intake to ICSR submissions`,
		},
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: { id: true, name: true, email: true },
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	// If there's a Better Auth session but no matching DB user, avoid forcing a logout loop here.
	// We'll treat the user as unauthenticated in the UI until the DB user exists.
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme: _theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className={`light h-full overflow-x-hidden antialiased`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				<GoogleAnalytics
					nonce={nonce}
					enabled={Boolean(allowIndexing && env?.MODE === 'production')}
				/>
			</head>
			<body className="bg-background text-foreground data-[scroll-locked]:!mr-0 data-[scroll-locked]:!overflow-visible data-[scroll-locked]:!pr-0">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	useToast(data.toast)
	const location = useLocation()
	const pathname = location.pathname
	const search = location.search

	// Track page views on route changes (SPA)
	useEffect(() => {
		if (typeof window === 'undefined') return
		if (window.ENV?.ALLOW_INDEXING === 'false') return
		if (window.ENV?.MODE !== 'production') return
		if (typeof window.gtag === 'function') {
			window.gtag('event', 'page_view', {
				page_title: document.title,
				page_location: window.location.href,
				page_path: `${pathname}${search}`,
			})
		}
	}, [pathname, search])

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<Outlet />
			<Toaster closeButton position="top-center" />
			<ProgressBar />
		</OpenImgContextProvider>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
